import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useCompany } from "@/lib/company";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChartPieSlice, ListChecks, Bank, ArrowsLeftRight, Car, CalendarCheck, SignOut, Wallet, UserCircle, List, X,
} from "@phosphor-icons/react";

const links = [
  { to: "/", label: "Genel Bakış", icon: ChartPieSlice, testid: "nav-dashboard" },
  { to: "/cekler", label: "Çekler", icon: ListChecks, testid: "nav-checks" },
  { to: "/alacaklar", label: "Cari Ekstre", icon: ArrowsLeftRight, testid: "nav-transactions" },
  { to: "/bankalar", label: "Banka Hesapları", icon: Bank, testid: "nav-banks" },
  { to: "/krediler", label: "Krediler", icon: Car, testid: "nav-loans" },
  { to: "/takvim", label: "Ödeme Takvimi", icon: CalendarCheck, testid: "nav-calendar" },
  { to: "/profil", label: "Profil & Kullanıcılar", icon: UserCircle, testid: "nav-profile" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { companies, selectedCompany, setSelectedCompany } = useCompany();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const content = (
    <>
      <div className="px-6 py-5 border-b border-border flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Wallet size={22} weight="duotone" className="text-primary" />
            <div className="font-heading font-extrabold text-lg tracking-tight">NAKİT</div>
          </div>
          <div className="text-xs text-muted-foreground">Nakit Yönetim Sistemi</div>
        </div>
        <button
          className="md:hidden text-muted-foreground p-2"
          onClick={() => setOpen(false)}
          data-testid="sidebar-close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="p-4 border-b border-border">
        <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
          Şirket
        </label>
        <Select value={selectedCompany} onValueChange={setSelectedCompany}>
          <SelectTrigger data-testid="company-selector" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Şirketler</SelectItem>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.id}`}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {links.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/70 hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <Icon size={18} weight="duotone" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-border p-4">
        <div className="text-xs text-muted-foreground mb-2 truncate">{user?.email}</div>
        <button
          data-testid="logout-button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="flex items-center gap-2 text-sm text-foreground/70 hover:text-destructive"
        >
          <SignOut size={16} /> Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
        <button onClick={() => setOpen(true)} className="p-2" data-testid="sidebar-toggle">
          <List size={22} />
        </button>
        <div className="flex items-center gap-2">
          <Wallet size={20} weight="duotone" className="text-primary" />
          <span className="font-heading font-extrabold text-base">NAKİT</span>
        </div>
        <div className="w-9" />
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-card border-r border-border h-screen sticky top-0 flex-col">
        {content}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setOpen(false)}
          />
          <aside className="md:hidden fixed top-0 left-0 bottom-0 w-72 bg-card z-50 flex flex-col shadow-xl">
            {content}
          </aside>
        </>
      )}
    </>
  );
}
